export interface CompatibleBrand {
  name: string;
  category: string;
  description: string;
  integrationLevel: 'Direct' | 'Gateway' | 'Matter' | 'Partner';
  popularity: 'High' | 'Medium' | 'Low';
  website?: string;
  logoUrl?: string;
}

export const BRAND_CATEGORIES = [
  'Smart Home Platforms',
  'Huishoudelijke Apparaten', 
  'Klimaatbeheersing',
  'Verlichting',
  'Audio & Entertainment',
  'Beveiliging & Toegang',
  'Raamdecoratie',
  'Energie & Solar',
  'EV Charging',
  'Sensoren & Monitoring',
  'Professional & KNX'
] as const;

export const COMPATIBLE_BRANDS: CompatibleBrand[] = [
  // Smart Home Platforms & Assistants
  {
    name: "Apple HomeKit",
    category: "Smart Home Platforms",
    description: "Volledige iOS ecosysteem integratie en Siri voice control",
    integrationLevel: "Direct",
    popularity: "High"
  },
  {
    name: "Google Home",
    category: "Smart Home Platforms", 
    description: "Google Assistant spraakbesturing en automatisering",
    integrationLevel: "Direct",
    popularity: "High"
  },
  {
    name: "Amazon Alexa",
    category: "Smart Home Platforms",
    description: "Alexa voice control en smart home routines",
    integrationLevel: "Direct", 
    popularity: "High"
  },
  {
    name: "Samsung SmartThings",
    category: "Smart Home Platforms",
    description: "IoT hub en device management platform",
    integrationLevel: "Gateway",
    popularity: "High"
  },
  {
    name: "Zigbee",
    category: "Smart Home Platforms",
    description: "Draadloos mesh communicatieprotocol",
    integrationLevel: "Direct",
    popularity: "High"
  },
  {
    name: "Z-Wave",
    category: "Smart Home Platforms", 
    description: "Smart home mesh netwerkstandaard",
    integrationLevel: "Gateway",
    popularity: "Medium"
  },
  {
    name: "Matter",
    category: "Smart Home Platforms",
    description: "Nieuwe universele smart home standaard",
    integrationLevel: "Matter",
    popularity: "Medium"
  },

  // Huishoudelijke Apparaten (Home Connect)
  {
    name: "Bosch",
    category: "Huishoudelijke Apparaten",
    description: "Wasmachines, vaatwassers en ovens met Home Connect",
    integrationLevel: "Partner",
    popularity: "High"
  },
  {
    name: "Siemens",
    category: "Huishoudelijke Apparaten",
    description: "Premium keukenapparatuur met slimme connectiviteit",
    integrationLevel: "Partner", 
    popularity: "High"
  },
  {
    name: "Miele",
    category: "Huishoudelijke Apparaten",
    description: "Luxe huishoudelijke apparaten met Miele@home",
    integrationLevel: "Partner",
    popularity: "Medium"
  },
  {
    name: "AEG",
    category: "Huishoudelijke Apparaten",
    description: "Moderne keukentoestellen met ConnectLife app",
    integrationLevel: "Gateway",
    popularity: "Medium"
  },
  {
    name: "Neff", 
    category: "Huishoudelijke Apparaten",
    description: "Inbouw keukenapparatuur met Home Connect",
    integrationLevel: "Partner",
    popularity: "Medium"
  },
  {
    name: "Gaggenau",
    category: "Huishoudelijke Apparaten", 
    description: "High-end cooking appliances en wine coolers",
    integrationLevel: "Partner",
    popularity: "Low"
  },

  // Klimaatbeheersing & HVAC
  {
    name: "Daikin",
    category: "Klimaatbeheersing",
    description: "Airco en warmtepompen met Daikin Online Controller",
    integrationLevel: "Direct",
    popularity: "High"
  },
  {
    name: "Mitsubishi Electric",
    category: "Klimaatbeheersing",
    description: "Climate control systemen en heat pumps",
    integrationLevel: "Gateway",
    popularity: "High"
  },
  {
    name: "LG",
    category: "Klimaatbeheersing",
    description: "Multi-split airconditioners met ThinQ technologie",
    integrationLevel: "Gateway",
    popularity: "Medium"
  },
  {
    name: "Gree",
    category: "Klimaatbeheersing",
    description: "Airco units met directe Loxone integratie",
    integrationLevel: "Direct",
    popularity: "Medium"
  },
  {
    name: "Nest",
    category: "Klimaatbeheersing",
    description: "Slimme thermostaten met learning algorithms",
    integrationLevel: "Gateway",
    popularity: "High"
  },
  {
    name: "Tado",
    category: "Klimaatbeheersing", 
    description: "Smart heating control en radiator thermostaten",
    integrationLevel: "Gateway",
    popularity: "Medium"
  },
  {
    name: "ÖkoFEN",
    category: "Klimaatbeheersing",
    description: "Pellet verwarmingssystemen en biomassa ketels",
    integrationLevel: "Direct",
    popularity: "Low"
  },

  // Verlichting
  {
    name: "Philips Hue",
    category: "Verlichting",
    description: "Slimme LED verlichting met 16 miljoen kleuren",
    integrationLevel: "Direct",
    popularity: "High"
  },
  {
    name: "LIFX",
    category: "Verlichting",
    description: "Color changing WiFi LED bulbs zonder hub",
    integrationLevel: "Gateway",
    popularity: "Medium"
  },
  {
    name: "Osram Lightify",
    category: "Verlichting", 
    description: "Professional lighting solutions en LED strips",
    integrationLevel: "Gateway",
    popularity: "Medium"
  },
  {
    name: "Nanoleaf",
    category: "Verlichting",
    description: "Design LED panels en shapes voor accent lighting",
    integrationLevel: "Gateway",
    popularity: "Medium"
  },
  {
    name: "Jung",
    category: "Verlichting",
    description: "Premium schakelaars en dimmer systemen",
    integrationLevel: "Direct",
    popularity: "High"
  },
  {
    name: "Gira",
    category: "Verlichting",
    description: "Luxe bedieningselementen en KNX componenten",
    integrationLevel: "Direct",
    popularity: "High"
  },
  {
    name: "Berker",
    category: "Verlichting",
    description: "Design schakelaars en stopcontacten",
    integrationLevel: "Direct",
    popularity: "Medium"
  },

  // Audio & Entertainment  
  {
    name: "Sonos",
    category: "Audio & Entertainment",
    description: "Multiroom audio systemen met streaming services",
    integrationLevel: "Direct",
    popularity: "High"
  },
  {
    name: "KEF",
    category: "Audio & Entertainment",
    description: "Premium speakers en wireless audiojsystemen",
    integrationLevel: "Gateway",
    popularity: "Medium"
  },
  {
    name: "Bose",
    category: "Audio & Entertainment",
    description: "High-quality audio en noise cancelling technology",
    integrationLevel: "Gateway",
    popularity: "High"
  },
  {
    name: "Denon",
    category: "Audio & Entertainment",
    description: "Professional audio en AV receivers",
    integrationLevel: "Gateway",
    popularity: "Medium"
  },
  {
    name: "Yamaha",
    category: "Audio & Entertainment",
    description: "Audio/video receivers en MusicCast systemen",
    integrationLevel: "Gateway",
    popularity: "Medium"
  },
  {
    name: "Bang & Olufsen",
    category: "Audio & Entertainment",
    description: "Luxury audio met Scandinavisch design",
    integrationLevel: "Gateway",
    popularity: "Low"
  },
  {
    name: "Bluesound",
    category: "Audio & Entertainment",
    description: "Hi-res wireless audio en multiroom streaming",
    integrationLevel: "Gateway",
    popularity: "Medium"
  },

  // Beveiliging & Toegang
  {
    name: "Nuki",
    category: "Beveiliging & Toegang",
    description: "Smart door locks met app en auto-unlock",
    integrationLevel: "Direct",
    popularity: "High"
  },
  {
    name: "Yale",
    category: "Beveiliging & Toegang",
    description: "Digital door locks en access control systemen",
    integrationLevel: "Gateway",
    popularity: "High"
  },
  {
    name: "Ring",
    category: "Beveiliging & Toegang",
    description: "Video doorbells en security camera's",
    integrationLevel: "Gateway",
    popularity: "High"
  },
  {
    name: "Axis",
    category: "Beveiliging & Toegang",
    description: "Professional security cameras en video analytics",
    integrationLevel: "Direct",
    popularity: "Medium"
  },
  {
    name: "Hikvision",
    category: "Beveiliging & Toegang",
    description: "IP surveillance systemen en NVR recorders",
    integrationLevel: "Direct",
    popularity: "Medium"
  },
  {
    name: "Dahua",
    category: "Beveiliging & Toegang",
    description: "IP camera's en video management systems",
    integrationLevel: "Direct",
    popularity: "Medium"
  },

  // Raamdecoratie & Zonwering
  {
    name: "Somfy",
    category: "Raamdecoratie",
    description: "Automatische zonwering en RTS/io-homecontrol motoren",
    integrationLevel: "Direct",
    popularity: "High"
  },
  {
    name: "Hunter Douglas",
    category: "Raamdecoratie",
    description: "Motorized blinds en PowerView automation",
    integrationLevel: "Gateway",
    popularity: "Medium"
  },
  {
    name: "Warema",
    category: "Raamdecoratie",
    description: "Sun shading systems en weather stations",
    integrationLevel: "Direct",
    popularity: "Medium"
  },
  {
    name: "Renson",
    category: "Raamdecoratie",
    description: "Outdoor living solutions en ventilatie systemen",
    integrationLevel: "Gateway",
    popularity: "Low"
  },

  // Energie & Solar
  {
    name: "Fronius",
    category: "Energie & Solar",
    description: "Solar inverters en energy management systemen",
    integrationLevel: "Direct",
    popularity: "High"
  },
  {
    name: "SolarEdge",
    category: "Energie & Solar",
    description: "Power optimizers en monitoring platforms",
    integrationLevel: "Direct",
    popularity: "High"
  },
  {
    name: "Huawei",
    category: "Energie & Solar",
    description: "Smart energy solutions en battery storage",
    integrationLevel: "Gateway",
    popularity: "Medium"
  },
  {
    name: "Tesla",
    category: "Energie & Solar",
    description: "Powerwall battery storage en solar systems",
    integrationLevel: "Gateway",
    popularity: "High"
  },
  {
    name: "BYD",
    category: "Energie & Solar",
    description: "Battery storage systems en energy management",
    integrationLevel: "Gateway",
    popularity: "Medium"
  },
  {
    name: "Victron Energy",
    category: "Energie & Solar",
    description: "Power management en off-grid solutions",
    integrationLevel: "Direct",
    popularity: "Medium"
  },
  {
    name: "SMA",
    category: "Energie & Solar",
    description: "Solar technology en energy systems",
    integrationLevel: "Direct",
    popularity: "High"
  },

  // EV Charging
  {
    name: "Tesla",
    category: "EV Charging",
    description: "Wall Connector en Supercharger network",
    integrationLevel: "Gateway",
    popularity: "High"
  },
  {
    name: "Wallbox",
    category: "EV Charging",
    description: "Smart charging stations met app controle",
    integrationLevel: "Direct",
    popularity: "High"
  },
  {
    name: "ABB",
    category: "EV Charging",
    description: "EV charging solutions en DC fast chargers",
    integrationLevel: "Gateway",
    popularity: "Medium"
  },
  {
    name: "KEBA",
    category: "EV Charging",
    description: "KeContact wallboxes met RFID authenticatie",
    integrationLevel: "Direct",
    popularity: "Medium"
  },
  {
    name: "Heidelberg",
    category: "EV Charging",
    description: "Wallbox Home Eco en Energy Control",
    integrationLevel: "Gateway",
    popularity: "Medium"
  },

  // Sensoren & Monitoring
  {
    name: "Netatmo",
    category: "Sensoren & Monitoring",
    description: "Weather stations en indoor air quality monitors",
    integrationLevel: "Gateway",
    popularity: "High"
  },
  {
    name: "Eve",
    category: "Sensoren & Monitoring",
    description: "HomeKit sensors voor temperature, humidity, air quality",
    integrationLevel: "Gateway",
    popularity: "Medium"
  },
  {
    name: "Aqara",
    category: "Sensoren & Monitoring",
    description: "Smart sensors en switches met Zigbee",
    integrationLevel: "Gateway",
    popularity: "Medium"
  },
  {
    name: "Fibaro",
    category: "Sensoren & Monitoring",
    description: "Z-Wave sensors voor motion, temperature, flood",
    integrationLevel: "Gateway",
    popularity: "Medium"
  },
  {
    name: "Shelly",
    category: "Sensoren & Monitoring",
    description: "WiFi switches, sensors en energy meters",
    integrationLevel: "Gateway",
    popularity: "High"
  },

  // Professional & KNX Integration
  {
    name: "Schneider Electric",
    category: "Professional & KNX",
    description: "Building automation en KNX componenten",
    integrationLevel: "Direct",
    popularity: "Medium"
  },
  {
    name: "ABB",
    category: "Professional & KNX",
    description: "Industrial automation en KNX devices",
    integrationLevel: "Direct",
    popularity: "Medium"
  },
  {
    name: "Siemens",
    category: "Professional & KNX",
    description: "Building technologies en HVAC automation",
    integrationLevel: "Direct",
    popularity: "Medium"
  },
  {
    name: "Theben",
    category: "Professional & KNX",
    description: "Time switches, sensors en KNX actors",
    integrationLevel: "Direct",
    popularity: "Low"
  },
  {
    name: "Elsner Elektronik",
    category: "Professional & KNX",
    description: "Weather sensors en automation controllers",
    integrationLevel: "Direct",
    popularity: "Low"
  },
  {
    name: "MDT",
    category: "Professional & KNX",
    description: "KNX devices en glass push buttons",
    integrationLevel: "Direct",
    popularity: "Low"
  }
];

export const getPopularBrands = () => 
  COMPATIBLE_BRANDS.filter(brand => brand.popularity === 'High');

export const getBrandsByCategory = (category: string) =>
  COMPATIBLE_BRANDS.filter(brand => brand.category === category);

export const searchBrands = (query: string) =>
  COMPATIBLE_BRANDS.filter(brand => 
    brand.name.toLowerCase().includes(query.toLowerCase()) ||
    brand.description.toLowerCase().includes(query.toLowerCase())
  );